import { useEffect, useState } from "react";
import { Share2, Lock, Home, Users, Trash2, Plus, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* Per-trip sharing controls (owner only): Private / Household / Selected People
   with view|edit roles. Invited guests don't need a membership — growth loop. */

const OPTIONS = [
  { id: "private", label: "Private", icon: Lock, desc: "Only me" },
  { id: "household", label: "Household", icon: Home, desc: "Everyone on my Couple/Family plan" },
  { id: "selected", label: "Selected people", icon: Users, desc: "Only people I choose (by email)" },
];

const CIRCLE = [
  { id: "partner", label: "Spouse / Partner", perm: "edit" },
  { id: "companion", label: "Travel companion", perm: "edit" },
  { id: "family", label: "Family member", perm: "view" },
  { id: "emergency", label: "Emergency contact", perm: "view" },
  { id: "guest", label: "Temporary guest", perm: "view" },
];
const circleLabel = (id) => CIRCLE.find((c) => c.id === id)?.label;

export default function TripSharing({ trip, onChange }) {
  const [privacy, setPrivacy] = useState(trip.privacy || "private");
  const [shares, setShares] = useState(trip.shared_with || []);
  const [hhRole, setHhRole] = useState(trip.household_role || "view");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("view");
  const [circle, setCircle] = useState("family");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (next = {}) => {
    const body = { privacy, shared_with: shares, household_role: hhRole, ...next };
    setBusy(true);
    try {
      const r = await api.put(`/trips/${trip.id}/sharing`, body);
      setPrivacy(r.data.privacy); setShares(r.data.shared_with); setHhRole(r.data.household_role);
      toast.success("Sharing updated");
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update sharing"); }
    finally { setBusy(false); }
  };

  const addShare = () => {
    const em = email.trim().toLowerCase();
    if (!em.includes("@")) { toast.error("Enter a valid email"); return; }
    if (shares.some((s) => s.email === em)) { toast.error("Already shared with this email"); return; }
    const next = [...shares, { email: em, role, circle_role: circle, expires_at: expires || null }];
    setShares(next); setEmail(""); setExpires("");
    save({ shared_with: next, privacy: "selected" });
  };

  const removeShare = (em) => {
    const next = shares.filter((s) => s.email !== em);
    setShares(next);
    save({ shared_with: next });
  };

  return (
    <section className="mb-10" data-testid="trip-sharing">
      <h2 className="font-heading font-900 text-sky-700 text-2xl flex items-center gap-2 mb-4">
        <Share2 className="w-6 h-6" /> Sharing &amp; Visibility
      </h2>
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        {OPTIONS.map((o) => (
          <button key={o.id} onClick={() => { setPrivacy(o.id); save({ privacy: o.id }); }} disabled={busy}
            data-testid={`sharing-${o.id}`}
            className={`text-left border-2 p-4 transition-colors ${privacy === o.id ? "border-sky-600 bg-sky-50" : "border-zinc-300 bg-white hover:border-sky-300"}`}>
            <div className="font-700 text-sm flex items-center gap-1.5"><o.icon className="w-4 h-4" /> {o.label}</div>
            <div className="text-xs text-muted-foreground mt-1">{o.desc}</div>
          </button>
        ))}
      </div>

      {privacy === "household" && (
        <div className="border border-zinc-300 bg-white p-4 mb-4 flex items-center gap-3 text-sm" data-testid="sharing-household-role">
          <span className="font-600">Household members can:</span>
          <select value={hhRole} onChange={(e) => { setHhRole(e.target.value); save({ household_role: e.target.value }); }}
            data-testid="household-role-select" className="border border-zinc-300 px-2 py-1.5 bg-white">
            <option value="view">View this trip</option>
            <option value="edit">View &amp; edit this trip</option>
          </select>
        </div>
      )}

      {privacy === "selected" && (
        <div className="border border-zinc-300 bg-white p-4" data-testid="sharing-selected-panel">
          <div className="flex flex-wrap gap-2 mb-3">
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="traveler@email.com"
              data-testid="share-email-input"
              className="flex-1 min-w-[180px] border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
            <select value={circle} data-testid="share-circle-select"
              onChange={(e) => { setCircle(e.target.value); setRole(CIRCLE.find((c) => c.id === e.target.value)?.perm || "view"); }}
              className="border border-zinc-300 px-2 py-2 text-sm bg-white">
              {CIRCLE.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="share-role-select"
              className="border border-zinc-300 px-2 py-2 text-sm bg-white">
              <option value="view">Can view</option>
              <option value="edit">Can edit</option>
              <option value="finance">Financial manager</option>
            </select>
            <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="share-expires-input"
              title="Optional — access ends automatically after this date"
              className="border border-zinc-300 px-2 py-2 text-sm bg-white" />
            <button onClick={addShare} disabled={busy} data-testid="share-add-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-sky-700 text-white text-sm font-700 hover:bg-sky-600 disabled:opacity-50">
              <Plus className="w-4 h-4" /> Invite
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-1 mb-3">
            My Travel Circle: pick a role (permission is pre-set but adjustable) and an optional expiry —
            access ends automatically after that date. <b>Financial manager</b> can view the plan and manage
            the trip's expenses &amp; settlement. Passports &amp; sensitive documents are never shared with anyone.
          </p>
          {shares.length === 0 ? (
            <p className="text-xs text-muted-foreground">No one yet — invite friends or relatives by email. They just need a free TravelerPulse account to see this trip.</p>
          ) : (
            <div className="space-y-1.5">
              {shares.map((s) => {
                const expired = s.expires_at && s.expires_at < new Date().toISOString().slice(0, 10);
                return (
                <div key={s.email} className={`flex flex-wrap items-center gap-2 text-sm ${expired ? "opacity-50" : ""}`} data-testid={`share-row-${s.email}`}>
                  <span className="flex-1 truncate min-w-[140px]">{s.email}</span>
                  {s.circle_role && <span className="text-xs px-1.5 py-0.5 border bg-violet-50 border-violet-300 text-violet-800">{circleLabel(s.circle_role)}</span>}
                  <span className={`text-xs px-1.5 py-0.5 border ${s.role === "edit" ? "bg-amber-50 border-amber-300 text-amber-800" : s.role === "finance" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-zinc-50 border-zinc-300 text-zinc-600"}`}>{s.role === "edit" ? "Can edit" : s.role === "finance" ? "Financial manager" : "Can view"}</span>
                  {s.expires_at && <span className={`text-xs px-1.5 py-0.5 border ${expired ? "bg-rose-50 border-rose-300 text-rose-700" : "bg-zinc-50 border-zinc-300 text-zinc-600"}`}>{expired ? "expired" : `until ${s.expires_at}`}</span>}
                  <button onClick={() => removeShare(s.email)} className="text-muted-foreground hover:text-destructive" data-testid={`share-remove-${s.email}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ); })}
            </div>
          )}
        </div>
      )}
      <SummaryLink trip={trip} />
      <CountdownShare tripId={trip.id} tripTitle={trip.title} />
    </section>
  );
}

/* Summary share link — family sees the plan; documents, PNRs, tickets & seats stay private. */
function SummaryLink({ trip }) {
  const [share, setShare] = useState(trip.share_summary || null);
  const [include, setInclude] = useState({ timeline: true, hotels: true, pickups: true, travelers: false, ...(trip.share_summary?.include || {}) });
  const [busy, setBusy] = useState(false);
  const link = share?.token ? `${window.location.origin}/trip-share/${share.token}` : null;

  const apply = async (enabled, inc = include) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/share-link`, { enabled, include: inc });
      setShare(enabled ? { token: data.token, include: data.include } : null);
      toast.success(enabled ? "Summary link ready" : "Summary link revoked");
    } catch { toast.error("Could not update the summary link"); }
    finally { setBusy(false); }
  };

  const toggle = (k) => {
    const inc = { ...include, [k]: !include[k] };
    setInclude(inc);
    if (share) apply(true, inc);
  };

  return (
    <div className="mt-4 border-t border-zinc-200 pt-4" data-testid="summary-share-link">
      <div className="text-sm font-700 flex items-center gap-1.5"><LinkIcon className="w-4 h-4 text-emerald-600" /> Summary link for family &amp; friends</div>
      <p className="text-xs text-muted-foreground mt-0.5 mb-2">
        Anyone with the link sees the plan only — <strong>never</strong> documents, PNRs, ticket photos, seats or contact details.
      </p>
      <div className="flex flex-wrap gap-3 mb-2">
        {[["timeline", "Flights & transport"], ["hotels", "Hotels"], ["pickups", "Pickups"], ["travelers", "Traveller names"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-1.5 text-xs">
            <input type="checkbox" checked={!!include[k]} onChange={() => toggle(k)} data-testid={`summary-inc-${k}`} className="w-3.5 h-3.5 accent-emerald-600" /> {l}
          </label>
        ))}
      </div>
      {share ? (
        <div className="flex flex-wrap items-center gap-2">
          <code className="text-xs bg-zinc-50 border border-zinc-200 px-2 py-1.5 truncate max-w-[260px]" data-testid="summary-link-url">{link}</code>
          <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }} data-testid="summary-link-copy"
            className="px-3 py-1.5 text-xs font-bold border border-zinc-300 hover:border-primary">Copy</button>
          <a href={`https://wa.me/?text=${encodeURIComponent(`Our trip plan: ${trip.title}\n${link}`)}`} target="_blank" rel="noreferrer"
            className="px-3 py-1.5 text-xs font-bold border border-emerald-500 text-emerald-700 hover:bg-emerald-50">WhatsApp</a>
          <button onClick={() => apply(false)} disabled={busy} data-testid="summary-link-revoke"
            className="px-3 py-1.5 text-xs font-bold border border-red-300 text-red-600 hover:bg-red-50">Revoke</button>
        </div>
      ) : (
        <button onClick={() => apply(true)} disabled={busy} data-testid="summary-link-create"
          className="px-4 py-2 text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
          {busy ? "Creating…" : "Create summary link"}
        </button>
      )}
    </div>
  );
}

/* Owner-only: public trip-countdown widget (shareable link + iframe embed). */
export function CountdownShare({ tripId, tripTitle }) {
  const [share, setShare] = useState(null); // {enabled, token, path, views}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/embed/countdown/trip/${tripId}`).then((r) => setShare(r.data)).catch(() => setShare({ enabled: false }));
  }, [tripId]);

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      const r = await api.post(`/embed/countdown/trip/${tripId}`, { enabled });
      setShare(r.data);
      toast.success(enabled ? "Countdown widget is live" : "Countdown turned off");
    } catch { toast.error("Could not update the countdown widget"); }
    setBusy(false);
  };

  const url = share?.enabled ? `${window.location.origin}${share.path}` : "";
  const iframe = share?.enabled
    ? `<iframe src="${url}?embed=1" width="360" height="340" style="border:0" title="Trip countdown"></iframe>`
    : "";

  return (
    <div className="border border-indigo-300 bg-indigo-50/50 p-4 mt-4" data-testid="countdown-share">
      <div className="font-700 text-sm text-indigo-900">Trip countdown widget</div>
      <p className="text-xs text-muted-foreground mt-0.5 mb-3">
        A public "days to go" card for {tripTitle ? `"${tripTitle}"` : "this trip"} — post the link or embed
        it on any site. Shows only the title, dates and stops.
      </p>
      {!share ? <span className="text-xs text-muted-foreground">Loading…</span> : share.enabled ? (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <code className="flex-1 min-w-[200px] bg-white border border-zinc-300 px-2 py-1.5 text-xs truncate" data-testid="countdown-share-url">{url}</code>
            <button onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copied"); }} data-testid="countdown-share-copy"
              className="border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-700 hover:border-indigo-600">Copy link</button>
            <button onClick={() => { navigator.clipboard?.writeText(iframe); toast.success("Embed code copied"); }} data-testid="countdown-share-copy-iframe"
              className="border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-700 hover:border-indigo-600">Copy embed code</button>
          </div>
          <div className="flex items-center gap-3 text-xs">
            {share.views > 0 && <span className="text-indigo-800 font-700" data-testid="countdown-share-views">👁 {share.views} view{share.views === 1 ? "" : "s"}</span>}
            <button onClick={() => toggle(false)} disabled={busy} data-testid="countdown-share-disable"
              className="font-700 text-rose-700 hover:underline disabled:opacity-50">Turn off</button>
            <a href="/embed" target="_blank" rel="noreferrer" className="font-700 text-indigo-700 hover:underline">Embed gallery →</a>
          </div>
        </div>
      ) : (
        <button onClick={() => toggle(true)} disabled={busy} data-testid="countdown-share-enable"
          className="bg-indigo-700 text-white px-4 py-2 text-xs font-700 hover:bg-indigo-600 disabled:opacity-50">
          {busy ? "Creating…" : "Create countdown link"}
        </button>
      )}
    </div>
  );
}
